<script setup lang="ts" generic="T">
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import type { DataTableCellEditCompleteEvent, DataTableFilterEvent, DataTableFilterMeta, DataTableRowClickEvent } from 'primevue/datatable';
import { ButtonType } from '../types/common';
import { TABLE_ROWS_OPTIONS } from 'vince-core/constants/common';

interface Props {
    title: string;
    showCount?: boolean;
    loading?: boolean;

    primaryButtonLabel?: string;
    primaryButtonIcon?: string;
    showPrimaryButton?: boolean;

    showSearch?: boolean;
    searchPlaceholder?: string;

    value: T[];

    scrollable?: boolean;
    scrollHeight?: string;
    paginator?: boolean;
    rows?: number;
    rowsPerPageOptions?: number[];
    filters?: DataTableFilterMeta;
    globalFilterFields?: string[];
    dataKey?: string;
    sortField?: string;
    sortOrder?: number;
    tableStyle?: string;
    rowClass?: (data: T) => string | object;
    applyInactiveRowStyle?: boolean;
    emptyMessage?: string;
    editMode?: 'cell' | 'row';
}

const props = withDefaults(defineProps<Props>(), {
    showCount: true,
    loading: false,
    showPrimaryButton: true,
    primaryButtonIcon: 'pi pi-plus',
    showSearch: true,
    scrollable: true,
    scrollHeight: 'calc(90vh - 200px)',
    paginator: true,
    rowsPerPageOptions: () => TABLE_ROWS_OPTIONS,
    tableStyle: 'min-width: 50rem',
    applyInactiveRowStyle: false
});

const emit = defineEmits<{
    'primary-action': [];
    'update:rows': [value: number];
    'filter': [event: DataTableFilterEvent];
    'cell-edit-complete': [event: DataTableCellEditCompleteEvent];
    'row-click': [event: DataTableRowClickEvent];
}>();

const { t } = useI18n();

const searchQuery = defineModel<string>('searchQuery', { default: '' });

const searchFilters = computed(() => ({
    global: {
        value: searchQuery.value,
        matchMode: 'contains'
    }
}));

const computedFilters = computed(() => props.filters || searchFilters.value);

const filteredCount = ref(0);

const setFilteredCount = (event: DataTableFilterEvent) => {
    filteredCount.value = event.filteredValue?.length ?? props.value.length;
    emit('filter', event);
};

watchEffect(() => {
    if (!props.loading) {
        filteredCount.value = props.value.length;
    }
});

const displayCount = computed(() =>
    props.filters ? filteredCount.value : props.value.length
);

const handlePrimaryAction = () => {
    emit('primary-action');
};

const computedRowClass = (data: T) => {
    if (props.rowClass) {
        return props.rowClass(data);
    }

    if (props.applyInactiveRowStyle && !(data as Record<string, unknown>).active) {
        return 'inactive-row';
    }

    return '';
};
</script>

<template>
    <div class="relative">
        <PageHeader>
            {{ title }}
            <VLSkeleton
                v-if="loading"
                width="27px"
                height="28px"
            />
            <span
                v-else-if="showCount"
            >
                ({{ displayCount }})
            </span>

            <template #actions>
                <slot name="filters" />
                <SearchInput
                    v-if="showSearch && !loading"
                    v-model="searchQuery"
                    :placeholder="searchPlaceholder || t('common.searchPlaceholder')"
                />
                <slot name="extra-actions" />
                <VButton
                    v-if="showPrimaryButton && primaryButtonLabel"
                    :label="primaryButtonLabel"
                    :icon="primaryButtonIcon"
                    class="ml-4"
                    :type="ButtonType.PRIMARY"
                    @click="handlePrimaryAction"
                />
            </template>
        </PageHeader>

        <div v-if="loading">
            <data-table-skeleton />
        </div>

        <VLDataTable
            v-else
            :value="value"
            :data-key="dataKey"
            :edit-mode="editMode"
            :scrollable="scrollable"
            :scroll-height="scrollHeight"
            :paginator="paginator"
            :rows="rows"
            :rows-per-page-options="rowsPerPageOptions"
            :filters="computedFilters"
            :global-filter-fields="globalFilterFields"
            :sort-field="sortField"
            :sort-order="sortOrder"
            :table-style="tableStyle"
            :row-class="computedRowClass"
            @update:rows="(val: number) => $emit('update:rows', val)"
            @filter="setFilteredCount"
            @cell-edit-complete="$emit('cell-edit-complete', $event)"
            @row-click="$emit('row-click', $event)"
        >
            <template #empty>
                <p class="text-center">
                    {{ emptyMessage || t('tables.emptyFilteredText') }}
                </p>
            </template>

            <slot />
        </VLDataTable>
    </div>
</template>

<style scoped>
:deep(.p-datatable-table-container) {
    border: 1px solid var(--main-border-color-dark);
}
:deep(.inactive-row) {
    background-color: var(--primary-text-disabled);
    opacity: 0.7;
}
</style>
